// Module dependencies
import canUseHistory from 'app/common/lib/util/canUseHistory';
import LruCache from 'app/common/lib/LruCache';
import SimpleBrowserHistory from 'app/common/lib/SimpleBrowserHistory';

/**
 * @fileoverview A history manager for the app. This keeps track of past urls
 * and the modules that were shown on those urls.
 */

const MAX_CACHED_ITEMS = 10;

/**
 * A record of the modules that were visited and any extra information
 * needed to identify/re-instate them.
 *
 * @param {string} url The url that was visited.
 * @param {string} moduleUid The module that was shown.
 * @param {?Array.<Module>} backgroundModuleUid The modules that were shown
 *    "behind" the current module ordered from back to front.
 * @constructor
 */
const HistoryRecord = function(url, moduleUid, backgroundModuleUid) {
  this.url = url;
  this.moduleUid = moduleUid;
  this.backgroundModuleUid = backgroundModuleUid || null;
  this.numHopsToBackground = 0;
};

/**
 * Packaged data returned by getModule that includes the module to use
 * and its background Module.
 *
 * @param {Module} module
 * @param {?Module} backgroundModule
 * @constructor
 */
const ModuleInfo = function(module, backgroundModule) {
  this.module = module;
  this.backgroundModule = backgroundModule || null;
};

/**
 * A history manager that uses push state.
 * @constructor
 */
const PushStateHistoryManager = function() {
  /**
   * @type {BrowserHistory}
   * @private
   */
  this._browserHistory = null;

  /**
   * Cache of previously seen modules.
   * @type {LruCache}
   * @private
   */
  this._seenModules = null;

  /**
   * A cache used to hold page titles and meta info.
   * @type {Object}
   */
  this._pageInfoCache = {};
  this._init();
};

Object.assign(
  PushStateHistoryManager.prototype,
  /** @lends {PushStateHistoryManager.prototype} */ {
    /**
     * Start the history manager, making the passed in module the first
     * module in the history. Multiple calls to the history module will
     * clear out the history and restart.
     *
     * @param {Module} initialModule The first module in the history.
     */
    start(initialModule) {
      if (initialModule) {
        initialModule.uid = this._getNewUid(initialModule);
        this._addToHistory(initialModule, null);
      }
    },

    _init() {
      this._seenModules = new LruCache(MAX_CACHED_ITEMS);
      /* Working on a bug with this. Will use simple history manager for now.
    if (window.history.state === undefined) {
      this._browserHistory = new SimpleBrowserHistory();
    } else {
      this._browserHistory = new BrowserHistory(function(historyRecord) {
        this._seenModules.remove(historyRecord.moduleUid);
        delete this._pageInfoCache[historyRecord.url];
      }.bind(this));
    }
    */
      this._browserHistory = new SimpleBrowserHistory();
    },

    /**
     * Append browser history data to navigation data
     *
     * @param {!NavigateData} navigateData
     */
    populateNavigateData(navigateData) {
      // Keep track of the last view's uid.
      navigateData.fromHistoryUid = this._browserHistory.getCurrentUid();
    },

    /**
     * Get the module that should be used for the window's current url.
     *
     * @param {Function} ModuleClass The class of the module (from the route).
     * @param {!NavigateData} navigateData
     * @return {ModuleInfo} The module to use for
     *    the current url and the module to show in the background.
     */
    getModule(ModuleClass, navigateData) {
      let module = null;
      let backgroundModule = null;
      let moduleUid = null;
      let historyRecord = null;

      if (navigateData.reuseLast) {
        historyRecord = this._browserHistory.get(navigateData.fromHistoryUid);
      } else {
        historyRecord = this._browserHistory.getCurrent();
      }

      if (historyRecord) {
        module = this._seenModules.get(historyRecord.moduleUid);
        moduleUid = historyRecord.moduleUid;
        backgroundModule = this._seenModules.get(historyRecord.backgroundModuleUid);
      }

      if (!module) {
        module = new ModuleClass();
        // If we have to re-create a previously seen module then use
        // the same uid so that the module cache will be correct.
        moduleUid = moduleUid !== null ? moduleUid : this._getNewUid(module);
        module.uid = moduleUid;
      }

      this._addToHistory(module, backgroundModule);
      return new ModuleInfo(module, backgroundModule);
    },

    /**
     * Updates the background modules for the current module and how many
     * 'view hops' from the main module to the background module.
     * @param {!NavigateData} navigateData
     */
    updateBackgroundModule(backgroundModule, navigateData) {
      const currentRecord = this._browserHistory.getCurrent();
      const lastRecord = this._browserHistory.get(navigateData.fromHistoryUid);
      if (backgroundModule) {
        currentRecord.backgroundModuleUid = backgroundModule.uid;
        this._seenModules.set(backgroundModule.uid, backgroundModule);
        if (navigateData.fromNavigate && lastRecord) {
          currentRecord.numHopsToBackground = lastRecord.numHopsToBackground + 1;
        }
      } else {
        currentRecord.backgroundModuleUid = null;
        currentRecord.numHopsToBackground = 0;
      }
    },

    /**
     * Cancel the navigation midway. This removes the cached module data
     * and then it goes back one step in history.
     */
    cancelNavigation() {
      const current = this._browserHistory.getCurrent();
      this._seenModules.remove(current.moduleUid);
      this._go(-1);
    },

    /**
     * Goes back in history to the current module's background module. Noop
     * if the current module doesn't have a background module.
     */
    closeCurrentModule() {
      const currentRecord = this._browserHistory.getCurrent();
      if (!currentRecord || currentRecord.numHopsToBackground === 0) {
        // module is not closeable don't do anything
        return;
      }
      this._go(-currentRecord.numHopsToBackground);
    },

    /**
     * Navigate to the previous page if the previous page is a Denzel page.
     * (Equivalent to hitting the back button on the browser).
     *
     * @return {boolean} Whether navigating back is possible.
     */
    back() {
      if (this._browserHistory.getHistoryRecordCount() > 1) {
        this._go(-1);
        return true;
      }
      return false;
    },

    setPageInfo(info) {
      const url = this._getUrl();
      this._pageInfoCache[url] = info;
    },

    getPageInfo(url) {
      url = url || this._getUrl();
      return this._pageInfoCache[url];
    },

    _go(numViews) {
      window.history.go(numViews);
    },

    _getUrl() {
      return window.location.href;
    },

    /**
     * Get a unique id for the module in the current browser session.
     *
     * @param {Module} module
     * @return {number}
     * @private
     */
    _getNewUid(module) {
      return module.cid;
    },

    /**
     * Add the module and background module to the browser history. This
     * creates a new history record if the view is new, otherwise it
     * re-uses the old history record and just marks in the cache that the
     * modules were seen.
     *
     * @param {Module} module The current main module.
     * @param {?Module} backgroundModule The current background module.
     * @private
     */
    _addToHistory(module, backgroundModule) {
      const url = this._getUrl();
      const backgroundModuleUid = backgroundModule ? backgroundModule.uid : null;
      let historyRecord = this._browserHistory.getCurrent();
      if (!historyRecord) {
        historyRecord = new HistoryRecord(url, module.uid, backgroundModuleUid);
      }
      this._browserHistory.add(historyRecord);
      this._seenModules.set(module.uid, module);
      if (backgroundModule) {
        this._seenModules.set(backgroundModuleUid, backgroundModule);
      }
    },
  },
);

/**
 * A history manager that doesn't use push state.
 * @constructor
 */
const NoPushStateHistoryManager = function() {
  /**
   * A cache used to hold page titles and meta info.
   * @type {Object}
   */
  this._pageInfoCache = {};
};

Object.assign(
  NoPushStateHistoryManager.prototype,
  /** @lends {NoPushStateHistoryManager.prototype} */ {
    start() {},
    populateNavigateData() {},
    getModule(ModuleClass, navigateData) {
      return [new ModuleClass({}), []];
    },
    updateBackgroundModule() {},
    cancelNavigation() {},
    closeCurrentModule() {},
    setPageInfo(info) {
      const url = this._getUrl();
      this._pageInfoCache[url] = info;
    },
    getPageInfo(url) {
      url = url || this._getUrl();
      return this._pageInfoCache[url];
    },
    _getUrl() {
      return window.location.href;
    },
  },
);

const HistoryManager = canUseHistory() ? PushStateHistoryManager : NoPushStateHistoryManager;

let _singleton = null;
HistoryManager.singleton = () => {
  if (!_singleton) {
    _singleton = new HistoryManager();
  }
  return _singleton;
};

export default HistoryManager;
